/**
 * Portalzugänge verwalten — gemeinsam genutzt von Admin → Kundenportal und
 * dem Tab "Zugang" im Kontakt. Passwörter sieht niemand: Kunden bekommen
 * einen Einladungs-/Zurücksetzen-Link und vergeben ihr Passwort selbst.
 */
import React, { useState } from 'react';
import { Copy, Mail, MessageCircle, Share2, Link2, Ban, ShieldCheck, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  createPortalLink, invitePortalUser, removePortalUser, setPortalUserBlocked,
} from '@/lib/adminSettingsApi';
import { cn } from '@/lib/utils';

export const PORTAL_STATUS = {
  eingeladen: { label: 'Eingeladen', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
  aktiv: { label: 'Aktiv', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  gesperrt: { label: 'Gesperrt', cls: 'bg-red-50 text-red-700 border-red-200' },
};

export function PortalStatusBadge({ status }) {
  const s = PORTAL_STATUS[status] || { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  return <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', s.cls)}>{s.label}</span>;
}

function shareText(name, link) {
  return [
    `Guten Tag${name ? ` ${name}` : ''},`,
    '',
    'über unser Kundenportal sehen Sie Ihre Bestellungen, bestellen nach und bezahlen offene Rechnungen.',
    'Bitte legen Sie über diesen persönlichen Link Ihr Passwort fest:',
    link,
    '',
    'Der Link ist aus Sicherheitsgründen nur begrenzt gültig. Falls er abgelaufen ist, senden wir Ihnen gern einen neuen.',
  ].join('\n');
}

/** Einladungs-/Zurücksetzen-Link weitergeben */
export function LinkShare({ link, email, name, telefon, onDone }) {
  const text = shareText(name, link);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success('Text mit Link kopiert'); } catch { toast.error('Kopieren fehlgeschlagen'); }
  };
  const share = async () => {
    if (!navigator.share) { copy(); return; }
    try { await navigator.share({ title: 'Ihr Zugang zum Kundenportal', text }); } catch { /* abgebrochen */ }
  };
  const phone = String(telefon || '').replace(/[^0-9]/g, '');
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3">
      <p className="text-sm text-emerald-900 font-medium flex items-center gap-2"><Link2 className="w-4 h-4" /> Persönlicher Link erstellt</p>
      <p className="text-xs text-emerald-900/80 break-all font-mono bg-white/70 rounded p-2 max-h-20 overflow-y-auto">{link}</p>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" onClick={share} className="bg-white"><Share2 className="w-4 h-4 mr-2" />Teilen…</Button>
        <Button type="button" variant="outline" onClick={copy} className="bg-white"><Copy className="w-4 h-4 mr-2" />Kopieren</Button>
        <Button type="button" variant="outline" asChild className="bg-white">
          <a href={`mailto:${email || ''}?subject=${encodeURIComponent('Ihr Zugang zum Kundenportal')}&body=${encodeURIComponent(text)}`}><Mail className="w-4 h-4 mr-2" />Per E-Mail</a>
        </Button>
        <Button type="button" variant="outline" asChild className="bg-white">
          <a href={`https://wa.me/${phone}?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp</a>
        </Button>
      </div>
      {onDone && <button type="button" onClick={onDone} className="text-xs text-emerald-900 underline">Schließen</button>}
    </div>
  );
}

/** Einladen-Formular für einen bekannten Kontakt */
export function InviteForm({ lead, onInvited, disabledReason }) {
  const [email, setEmail] = useState(lead?.email || '');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await invitePortalUser({ leadId: lead.id, email });
      toast.success(`Zugang für ${res.lead_name} angelegt`);
      onInvited?.({ ...res, telefon: lead.telefon });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
      <label className="flex-1">
        <span className="sr-only">E-Mail für den Portalzugang</span>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-Mail für den Login" className="h-11" required />
      </label>
      <Button type="submit" disabled={busy || !!disabledReason} title={disabledReason || undefined} className="h-11">
        <Send className="w-4 h-4 mr-2" />{busy ? 'Wird angelegt …' : 'Zugang anlegen'}
      </Button>
    </form>
  );
}

/** Aktionen für einen bestehenden Zugang */
export function PortalUserActions({ user, telefon, onChanged, compact = false }) {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const res = await fn();
      if (ok) toast.success(ok);
      onChanged?.();
      return res;
    } catch (err) {
      toast.error(err.message);
      return null;
    } finally {
      setBusy(null);
    }
  };

  const gesperrt = user.status === 'gesperrt';
  return (
    <div className="space-y-3">
      <div className={cn('flex flex-wrap gap-2', compact && 'justify-end')}>
        {!gesperrt && (
          <Button type="button" variant="outline" size="sm" disabled={!!busy} className="h-10"
            onClick={async () => { const r = await run('link', () => createPortalLink(user.id)); if (r?.link) setLink(r.link); }}>
            <Link2 className="w-4 h-4 mr-1.5" />{user.status === 'eingeladen' ? 'Einladung erneut' : 'Passwort-Link'}
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" disabled={!!busy} className="h-10"
          onClick={() => run('sperren', () => setPortalUserBlocked(user.id, !gesperrt), gesperrt ? 'Zugang entsperrt' : 'Zugang gesperrt')}>
          {gesperrt ? <><ShieldCheck className="w-4 h-4 mr-1.5" />Entsperren</> : <><Ban className="w-4 h-4 mr-1.5" />Sperren</>}
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={!!busy} className="h-10 text-red-700 hover:text-red-800" onClick={() => setConfirmRemove(true)}>
          <Trash2 className="w-4 h-4 mr-1.5" />Entfernen
        </Button>
      </div>
      {link && <LinkShare link={link} email={user.email} name={user.lead_name} telefon={telefon} onDone={() => setLink(null)} />}

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Portalzugang entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Login {user.email} wird gelöscht. Bestellungen, Rechnungen und Kontaktdaten bleiben unverändert erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700"
              onClick={() => run('remove', () => removePortalUser(user.id), 'Zugang entfernt')}>Entfernen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
